import { Component, inject, ViewChild } from '@angular/core';
import { Config } from '@config';
import { TenantService } from '@tenant';
import { DxAspNetDataXsrfAwareService } from '@tools';
import { UrlEnhancerService } from '@url';
import DataSource from 'devextreme/data/data_source';
import { MessagesService, MessageType } from '@ui-messages';
import { __i18n } from '@localisation';
import { LanguageService } from '@localisation';
import { UserService } from '@user';
import { RoutePrivilegeNames } from '@route-privilege-names';
import { PrivilegesService } from '@privileges';
import { PrivilegesLoadingService } from '@auth';
import { ClxFormComponent } from '@forms';
import { PlantService } from '@plants';
import { PlantListComponent } from 'modules/plants-module/operating/plant-list.component';
import { BatchUpgradeStatusService } from './batch-upgrade-status.service';
import { RoutingInfoService } from '@routing';
import { BatchUpgradeService } from './batch-upgrade.service';



@Component({
    selector: 'batch-upgrade-schedule-list',
    templateUrl: './batch-upgrade-schedule-list.template.html',
    standalone: false
})
export class BatchUpgradeScheduleListComponent {
    #routingInfoService: RoutingInfoService = inject(RoutingInfoService);
    #batchUpgradeService: BatchUpgradeService = inject(BatchUpgradeService);

    constructor(
        private dxAspNetXsrfAwareService: DxAspNetDataXsrfAwareService,
        private urlEnhancer: UrlEnhancerService,
        private config: Config,
        private tenantService: TenantService,
        private plantService: PlantService,
        private messageService: MessagesService,
        private languageService: LanguageService,
        private userService: UserService,
        private privilegeLoadingService: PrivilegesLoadingService,
        private privilegeService: PrivilegesService,
        public batchUpgradeStatusService: BatchUpgradeStatusService
    ) { }

    public isProxyTenant: boolean = false;

    #availablePlants: any;// [{ plantId: string, name: string }];

    public async ngOnInit() {


        this.allPlantIds = await this.getAllPlantIds();
        this.assignedSource = this.setDataSource(true);

        const tenant = await this.tenantService.getCurrentTenant();
        this.isProxyTenant = tenant.isProxy;
        this.applicationSets = await this.plantService.getBatchUpgradeApplicationSets();
        if (this.applicationSets && this.applicationSets.length >= 1) {
            this.applicationSets[0].selected = true;
        }

        const tenantId = this.tenantService.getCurrentTenantId();
        const locale = this.languageService.language;
        
        this.selectedPlants = [];
        this.#availablePlants = await this.#batchUpgradeService.getAvailablePlants(tenantId, locale);


        const routingParams = this.#routingInfoService.getCurrentRoutingParams();
        const batchUpgradeId = routingParams.batchupgradeid;
        if (batchUpgradeId) {
            const unsuccessfulPlants = await this.#batchUpgradeService.getUnsuccessfulPlantsByBatchUpgradeId(batchUpgradeId, tenantId, locale);
            // this.#availablePlants = unsuccessfulPlants.map(current => {
            //     return {
            //         plantId: current.siteId, name: current.name
            //     }
            // });
            const plantIds = unsuccessfulPlants.map(current => current.siteId);
            this.selectedPlants = plantIds;
        }
        // this.selectedPlantNames = availablePlants.data.map(current => { return { plantId: current.siteId, name: current.name} });
    }

    public remoteOperations: any = {
        paging: true, filtering: true, sorting: true, grouping: true, summary: true, groupPaging: true
    };

    public applicationSets: any[];

    public assignedSource: DataSource;

    public tenant: any;

    public subTenants: any[];

    public hasSystemPrivilege: boolean = false;

    public isInitialized: boolean = false;

    public allPlantIds: string[] = [];

    public showUpgradeStatus: any;

    @ViewChild(PlantListComponent)
    public grid: PlantListComponent


    public filterByAppplicationSet(value) {
        this.generateFilter(value)
    }


    private async getAllPlantIds(filter?) {
        const dataSource: any = this.setDataSource(true);
        dataSource.select(['SiteId']);
        dataSource.paginate(false);
        if (filter) {
            dataSource.filter(filter);
        }

        const result = await dataSource.load();

        return result.map((current) => {
            return current.SiteId;
        })
    }

    private filter: any;

    private async generateFilter(value) {
        const output = [];
        if (value?.length) {
            value.forEach((current, index) => {
                output.push(['ApplicationSetName', '=', current])
                if (index + 1 < value.length) {
                    output.push('or');
                }
            })
        }
        if (output.length) {
            this.grid.grid.dxDataGrid.instance.filter(output);
        } else {
            this.grid.grid.dxDataGrid.instance.filter(null);
        }

        this.updateSelectedPlants(output);
    }

    private async updateSelectedPlants(filter?) {
        this.allPlantIds = await this.getAllPlantIds(filter);
        this.plantSelectionChange(this.selectedPlants.filter((current) => {
            return this.allPlantIds.includes(current);
        }));
    }

    public async ngAfterViewInit() {
        this.tenant = await this.tenantService.getCurrentTenant();
        await this.privilegeLoadingService.waitForPrivileges();
        this.privileges = this.privilegeService.getPrivileges();

        this.hasSystemPrivilege = this.privileges.find(p => p.name === RoutePrivilegeNames.Systems) !== undefined;
        const canAssignPlants = this.privileges.find(p => p.name === RoutePrivilegeNames.PlantAssign) !== undefined;

        try {
            this.subTenants = canAssignPlants ? await this.tenantService.getSubtenantsForAssignPlant() : [];
            if (this.subTenants && this.subTenants.length) {
                this.showMoveToSubTenant = true;
                this.subTenants[0].selected = true;
            }
        } catch (e) {
            this.subTenants = [];
        }
        this.isInitialized = true;
    }

    public setDataSource(showAssigend) {
        const tenantId = this.tenantService.getCurrentTenantId();
        const locale = this.languageService.language;
        const url = this.urlEnhancer.replaceParams(this.config.services.plant.plantList);
        return new DataSource({
            store: this.dxAspNetXsrfAwareService.createStore({
                loadUrl: url,
                loadMethod: 'POST',
                key: 'SiteId',
                onBeforeSend: (op, settings) => {
                    settings.data.isAssigned = (showAssigend) ? 't' : 'f';
                    settings.data.tenantId = tenantId;
                    settings.data.locale = locale;
                    settings.data.isAdministrationPage = true;
                }
            })
        })
    }

    public plantTenant: string;

    public selectedPlant: string;

    public mainTenantValue: boolean = false;

    public subTenant: any;

    public showMoveToSubTenant: boolean = false;

    public showActivateButton: boolean = true;

    private privileges: any[];


    // batch upgrade
    @ViewChild('upgradeForm')
    public upgradeForm: ClxFormComponent;

    public openBatchUpgrade() {
        this.showBatchPopUp = true;
    }

    public closeBatchUpgrade() {
        this.showBatchPopUp = false;
    }
    public showBatchPopUp: boolean = false;


    public selectedPlantNames: any = [];
    public innerSelectedPlants: string[] = [];

    public set selectedPlants(value: string[]) {
        this.innerSelectedPlants = value;

        if(this.#availablePlants) {
            const selectedPlantNames = this.#availablePlants
                .filter(availablePlant => value.some(currentValue => currentValue === availablePlant.siteId));

            this.selectedPlantNames = selectedPlantNames;
        }
    }

    public get selectedPlants() {
        return this.innerSelectedPlants;
    }


    public plantSelectionChange(data) {
        this.selectedPlants = data;
    }

    public deselectPlant(plantId: string) {
        this.selectedPlants = this.selectedPlants.filter(current => current !== plantId);
    }

}